import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  resolveLocalizedEntry,
  langAttribute,
} from "../../../utils/questionUtils";
import { strings } from "../../../resources/strings";
import {
  IdentifiableAnswerV3Input,
  LocalizedParticipantQuestion,
  QuestionType,
  WorkflowName,
} from "../../../apis/API";
import { WorkflowItem, WorkflowSpec } from "./types";
import {
  parseWorkflowPrepopulation,
  serializeWorkflowAnswers,
} from "./workflowAnswers";
import {
  resolveStructuredFormLabel,
  withRequiredSuffix,
} from "./workflowLabels";
import { AnswerIcon, CHECK_PATH, saveButtonClass } from "./answerButtons";
import AnswerButtonRow from "./AnswerButtonRow";
import ChangeAnswerButton from "./ChangeAnswerButton";
import { resolveSeedItems } from "./historyPrefill";
import FloatingLabelField from "./FloatingLabelField";
import { getFieldError, getFieldInputHints } from "./fieldValidation";
import { getAutocompleteToken } from "./autocompleteTokens";
import type { WorkflowAnswer } from "./WorkflowQuestion";

const wf = strings.reportForm.workflow;

/** A resolved field: wire key + display label + whether it gates Save. */
export interface FieldLabel {
  key: string;
  label: string;
  required: boolean;
}

/** One editable entry (a row of field values), tracked by a stable client id. */
export interface Row {
  id: number;
  values: Record<string, string>;
}

export interface StructuredFormProps {
  question: LocalizedParticipantQuestion;
  /** Resolved workflow name (employment | vehicles | travel | … | officerReporting). */
  workflowName: WorkflowName;
  /** Resolved YES_NO_STRUCTURED_FORM spec. */
  spec: WorkflowSpec;
  /** Prior answer, for back-nav re-display (root selection + entered values). */
  defaultAnswer?: IdentifiableAnswerV3Input;
  /**
   * Raw `questionPrepopulation.values` for this workflow. Read only through the
   * `historyPrefill` helpers, which apply the history-eligibility list.
   */
  prepopulationValues?: string;
  onAnswer: (answer: WorkflowAnswer) => void;
}

/** All spec fields blank — the shape every row starts from. */
export const emptyValues = (spec: WorkflowSpec): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const { fieldKey } of spec.fields) {
    out[fieldKey] = "";
  }
  return out;
};

/**
 * Presentational, memoized field group for one entry. Memoized so typing in one
 * row (repeatable `vehicles`) re-renders only that row, not its siblings — the
 * parent hands it a stable `onChange` and a per-row `values` object whose
 * identity changes only when that row is edited.
 */
export const StructuredRow = React.memo<{
  fields: FieldLabel[];
  values: Record<string, string>;
  rowId: number;
  onChange: (rowId: number, key: string, value: string) => void;
  autoFocusFirst: boolean;
  /**
   * Owning workflow — required to resolve autofill tokens (SC 1.3.5). The
   * lookup cannot key on the field alone: `streetAddress`/`city`/`state`/
   * `zipCode` belong to the participant on `addressConfirmation` and to their
   * employer on `employment`.
   */
  workflowName: WorkflowName;
}>(({ fields, values, rowId, onChange, autoFocusFirst, workflowName }) => (
  <div className="flex flex-col gap-4">
    {fields.map((f, i) => {
      const { type, inputMode } = getFieldInputHints(f.key);
      return (
        // required marker + native required/aria-required come from the field
        // spec; Save is still enforced via canSave. First field of the first
        // row focuses. Format errors (e.g. a non-numeric vehicle year) are
        // surfaced inline and also gate canSave/canAddRow below.
        <FloatingLabelField
          key={f.key}
          label={withRequiredSuffix(f.label, f.required)}
          required={f.required}
          value={values[f.key]}
          testId={`structured-field-${rowId}-${f.key}`}
          autoFocus={autoFocusFirst && i === 0}
          onChange={(v) => onChange(rowId, f.key, v)}
          type={type}
          inputMode={inputMode}
          error={getFieldError(f.key, values[f.key])}
          autoComplete={getAutocompleteToken(workflowName, f.key)}
        />
      );
    })}
  </div>
));
StructuredRow.displayName = "StructuredRow";

/**
 * YES_NO_STRUCTURED_FORM workflow UI (Android `StructuredForm`). Three modes:
 *
 * - **Yes-gated non-repeatable** (`travel`, `feesPayment`): Yes → a multi-field
 *   form → `answer:true` + flat object; No → `answer:false` + `{}`.
 * - **Yes-gated repeatable** (`vehicles`, `employment`): Yes → a form with "Add
 *   another" → `answer:true` + an array of one object per entry wrapped under
 *   the workflow key (`serializeAs: "objectList"`, e.g. `{ employment: [{…}] }`);
 *   No → `answer:false` + the same key wrapping an empty array (`{ employment: [] }`).
 * - **ALWAYS** (`officerReporting`, `branchOpensOn:"ALWAYS"`): no root; the form
 *   shows immediately → `answer:null` + flat object.
 *
 * Yes-gated forms show a "Change my answer" control on the form step that steps
 * back to the Yes/No root (EHA-903); ALWAYS forms have no root, so they omit it.
 * The `answer` bool follows `branchOpensOn`, matching the Android
 * `V3AnswerAssembler` wire contract.
 */
const StructuredForm: React.FC<StructuredFormProps> = ({
  question,
  workflowName,
  spec,
  defaultAnswer,
  prepopulationValues,
  onAnswer,
}) => {
  const questionId = question.id ?? "";
  const wd = question.workflowDefinition;
  const isAlways = spec.branchOpensOn === "ALWAYS";
  const headlineEntry = resolveLocalizedEntry(question.localization);
  const headline = headlineEntry?.text ?? "";
  // SC 3.1.2 — the resolver falls back across languages, so text can arrive in
  // a language the document does not declare. undefined when it matches.
  const headlineLang = langAttribute(headlineEntry?.locale);

  // Field wire-keys → display labels (server-first, client-catalog fallback).
  const fields = useMemo<FieldLabel[]>(
    () =>
      spec.fields.map((f) => ({
        key: f.fieldKey,
        required: f.required,
        label:
          resolveStructuredFormLabel(workflowName, wd, "entry", f.fieldKey) ??
          f.fieldKey,
      })),
    [spec, workflowName, wd],
  );

  // Seed rows once per mount (the flow remounts the form on every navigation).
  // Repeatable reads the in-session answer, else the participant's last
  // submitted answer; non-repeatable reads the in-session flat object. Always
  // at least one row.
  const [rows, setRows] = useState<Row[]>(() => {
    if (spec.repeatable) {
      const items = resolveSeedItems({
        spec,
        workflowName,
        defaultAnswer,
        prepopulationValues,
      });
      const base = items.length > 0 ? items : [emptyValues(spec)];
      return base.map((values, i) => ({
        id: i,
        values: { ...emptyValues(spec), ...values },
      }));
    }
    const item = parseWorkflowPrepopulation(
      defaultAnswer?.workflowAnswers,
      spec,
    );
    return [{ id: 0, values: { ...emptyValues(spec), ...item } }];
  });
  // Next stable row id (never the array index — Remove must not reuse a row's id).
  const nextId = useRef(rows.length);

  // ALWAYS opens straight on the form; Yes-gated opens on the Yes/No root.
  const [step, setStep] = useState<"root" | "form">(isAlways ? "form" : "root");
  const rootAnswer = defaultAnswer?.answer ?? null;

  const entryTitle =
    resolveStructuredFormLabel(workflowName, wd, "entry", "title") ?? headline;
  const addMoreLabel =
    resolveStructuredFormLabel(workflowName, wd, "entry", "addMore") ??
    wf.addAnother;
  // Per-entry header noun for repeatable forms, e.g. "Vehicle" → "Vehicle #1"
  // or "Job" → "Job #1" (only shown for the repeatable specs: vehicles, employment).
  const itemLabel =
    resolveStructuredFormLabel(workflowName, wd, "entry", "itemLabel") ??
    "Item";

  const emit = (answer: boolean | null, items: WorkflowItem[]) =>
    onAnswer({
      questionId,
      questionType: QuestionType.workflow,
      workflowName,
      answer,
      workflowAnswers: serializeWorkflowAnswers(workflowName, spec, items),
    });

  const handleChange = useCallback(
    (rowId: number, key: string, value: string) => {
      setRows((prev) =>
        prev.map((r) =>
          r.id === rowId ? { ...r, values: { ...r.values, [key]: value } } : r,
        ),
      );
    },
    [],
  );

  const handleAddRow = () =>
    setRows((prev) => [
      ...prev,
      { id: nextId.current++, values: emptyValues(spec) },
    ]);

  // The Remove control only renders when there are ≥2 rows, so filtering always
  // leaves at least one — no separate guard needed (keeps ≥1 invariant via UI).
  const handleRemoveRow = (rowId: number) =>
    setRows((prev) => prev.filter((r) => r.id !== rowId));

  const requiredKeys = useMemo(
    () => spec.fields.filter((f) => f.required).map((f) => f.fieldKey),
    [spec],
  );

  // A repeatable row we can drop on Save: it has no content in any REQUIRED field
  // (an unused "Add another" row, or one with only stray optional input). Keying
  // this off required fields — not "every field blank" — avoids a partially-filled
  // optional-only row silently disabling Save. (Every structured workflow has ≥1
  // required field, so requiredKeys is non-empty here.)
  const rowIsDroppable = (r: Row) =>
    requiredKeys.every((k) => r.values[k].trim().length === 0);

  // True when every field with a rule (e.g. a vehicle `year`) either has no
  // value or matches its expected format — blocks Save on bad data (a letter
  // in the year field, digits-only in Owner's Name) the same way a missing
  // required field does.
  const rowFieldsAreValid = (r: Row) =>
    spec.fields.every(
      (f) => getFieldError(f.fieldKey, r.values[f.fieldKey]) === undefined,
    );

  const effectiveRows = spec.repeatable
    ? rows.filter((r) => !rowIsDroppable(r))
    : rows;
  const canSave =
    effectiveRows.length > 0 &&
    effectiveRows.every(
      (r) =>
        requiredKeys.every((k) => r.values[k].trim().length > 0) &&
        rowFieldsAreValid(r),
    );

  // Can't add another entry until the current (last) one's required fields are
  // filled and valid — avoids spawning trailing incomplete/invalid rows.
  const canAddRow =
    requiredKeys.every(
      (k) => rows[rows.length - 1].values[k].trim().length > 0,
    ) && rowFieldsAreValid(rows[rows.length - 1]);

  const handleSave = () => {
    const items: WorkflowItem[] = effectiveRows.map((r) => {
      const item: WorkflowItem = {};
      for (const { fieldKey } of spec.fields) {
        item[fieldKey] = r.values[fieldKey].trim();
      }
      return item;
    });
    emit(isAlways ? null : true, items);
  };

  const handleYes = () => setStep("form");
  const handleNo = () => emit(false, []);

  // --- Form step (Yes-gated after Yes, or ALWAYS from the start) -----------
  if (step === "form") {
    return (
      <div
        data-testid="structured-form"
        className="flex flex-col gap-6 overflow-y-auto"
      >
        <h2 className="text-2xl text-black">{entryTitle}</h2>

        {rows.map((row, rowIndex) => (
          <div key={row.id} className="flex flex-col gap-4">
            {spec.repeatable && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-primary">
                  {`${itemLabel} #${rowIndex + 1}`}
                </span>
                {rows.length > 1 && (
                  <button
                    type="button"
                    data-testid={`structured-remove-${row.id}`}
                    aria-label={`${wf.remove} ${rowIndex + 1}`}
                    onClick={() => handleRemoveRow(row.id)}
                    className="min-h-[44px] px-3 text-sm text-[#515964] underline"
                  >
                    {wf.remove}
                  </button>
                )}
              </div>
            )}
            <StructuredRow
              fields={fields}
              values={row.values}
              rowId={row.id}
              onChange={handleChange}
              autoFocusFirst={rowIndex === 0}
              workflowName={workflowName}
            />
          </div>
        ))}

        {spec.repeatable && (
          <button
            type="button"
            data-testid="structured-add"
            onClick={handleAddRow}
            disabled={!canAddRow}
            className="min-h-[44px] self-start px-3 font-semibold text-primary disabled:opacity-30"
          >
            + {addMoreLabel}
          </button>
        )}

        <button
          type="button"
          data-testid="structured-save"
          onClick={handleSave}
          disabled={!canSave}
          className={saveButtonClass}
        >
          <AnswerIcon testId="save-continue-icon" path={CHECK_PATH} />
          {wf.saveContinue}
        </button>

        {!isAlways && <ChangeAnswerButton onClick={() => setStep("root")} />}
      </div>
    );
  }

  // --- Root step (Yes-gated only) -----------------------------------------
  return (
    <div data-testid="structured-form" className="flex flex-col gap-6">
      <h2 lang={headlineLang} className="text-2xl text-black">
        {headline}
      </h2>

      <AnswerButtonRow
        onYes={handleYes}
        onNo={handleNo}
        yesSelected={rootAnswer === true}
        noSelected={rootAnswer === false}
        yesTestId="structured-yes"
        noTestId="structured-no"
      />
    </div>
  );
};

export default StructuredForm;
